/**
 * XR controller state, flattened into one stable per-frame snapshot.
 *
 * WebXR does not expose motion controllers through `navigator.getGamepads()`,
 * so none of this reaches `Input._pollGamepad`. It is polled directly off
 * `session.inputSources` instead and then pushed into the existing Input
 * snapshot (see XrSystem._poll), which is what lets the movement state machine,
 * the weapon and the pause menu keep working unmodified.
 *
 * Button indices follow the `xr-standard` gamepad mapping, which every 6DoF
 * controller Quest/Index/Pico/WMR ships reports:
 *
 *   0 trigger   1 squeeze   2 (touchpad, usually absent)   3 thumbstick press
 *   4 primary   (A on the right controller, X on the left)
 *   5 secondary (B on the right controller, Y on the left)
 *   axes[2..3]  thumbstick x/y   (axes[0..1] is the touchpad, if any)
 *
 * A controller that reports fewer buttons simply leaves the missing ones false;
 * nothing here indexes past what the device published.
 */

const EMPTY = Object.freeze({
  present: false,
  trigger: false, triggerValue: 0,
  squeeze: false,
  stickPressed: false,
  primary: false, secondary: false,
  x: 0, y: 0,
});

function readHand(source) {
  const pad = source?.gamepad;
  if (!pad) return EMPTY;
  const b = pad.buttons;
  const a = pad.axes;
  const btn = (i) => (b.length > i ? b[i].pressed === true : false);
  // Prefer the thumbstick pair. Devices with only a touchpad report it in
  // axes[0..1] and leave 2..3 absent, so fall back rather than reading zero.
  const hasStick = a.length > 3;
  return {
    present: true,
    trigger: btn(0),
    triggerValue: b.length > 0 ? b[0].value ?? (b[0].pressed ? 1 : 0) : 0,
    squeeze: btn(1),
    stickPressed: btn(3),
    primary: btn(4),
    secondary: btn(5),
    x: hasStick ? a[2] ?? 0 : a[0] ?? 0,
    y: hasStick ? a[3] ?? 0 : a[1] ?? 0,
  };
}

export class XrControls {
  constructor() {
    this.left = EMPTY;
    this.right = EMPTY;
    /** Set while the platform menu/system button is held, where reported. */
    this.menu = false;
    this._sources = null;
  }

  /**
   * @param {XRSession} session
   */
  poll(session) {
    this.left = EMPTY;
    this.right = EMPTY;
    if (!session) return this;
    const sources = session.inputSources;
    if (!sources) return this;
    for (let i = 0; i < sources.length; i++) {
      const s = sources[i];
      if (s.handedness === 'left') this.left = readHand(s);
      else if (s.handedness === 'right') this.right = readHand(s);
    }
    return this;
  }

  /**
   * The input source for a hand, so the caller can reach `targetRaySpace` or
   * check whether the device is tracked at all.
   */
  source(session, handedness) {
    const sources = session?.inputSources;
    if (!sources) return null;
    for (let i = 0; i < sources.length; i++) {
      if (sources[i].handedness === handedness) return sources[i];
    }
    return null;
  }
}

/** Deadzone + rescale, so the usable range still reaches 1.0. */
export function deadzone(v, dz) {
  const a = Math.abs(v);
  if (a < dz) return 0;
  return Math.sign(v) * ((a - dz) / (1 - dz));
}
